import { PublicClientApplication } from "@azure/msal-browser";
import { fetchFabricConfig, getCachedConfig } from "./services/fabricConfig";

// MSAL instance - will be initialized dynamically
let msalInstance = null;

// Default redirect URI
const REDIRECT_URI = process.env.REACT_APP_MSAL_REDIRECT_URI || window.location.origin + "/auth/callback";

/**
 * Create MSAL configuration from fabric config
 * @param {Object} fabricConfig - Configuration from fabric API
 * @returns {Object} MSAL configuration object
 */
const createMsalConfig = (fabricConfig) => ({
  auth: {
    clientId: fabricConfig.clientId,
    authority: `https://login.microsoftonline.com/${fabricConfig.tenantId}`,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: REDIRECT_URI,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case 0: // LogLevel.Error
            console.error(message);
            return;
          case 1: // LogLevel.Warning
            console.warn(message);
            return;
          case 2: // LogLevel.Info
            console.info(message);
            return;
          case 3: // LogLevel.Verbose
            console.debug(message);
            return;
          default:
            return;
        }
      },
    },
  },
});

/**
 * Initialize MSAL instance with fetched configuration
 * @returns {Promise<PublicClientApplication>} Initialized MSAL instance
 */
export const initializeMsal = async () => {
  if (msalInstance) {
    return msalInstance;
  }

  try {
    // Fetch configuration from API
    const fabricConfig = await fetchFabricConfig();
    
    // Create MSAL config
    const msalConfig = createMsalConfig(fabricConfig);
    
    // Create and initialize MSAL instance
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    
    console.log("MSAL initialized successfully with fabric config");
    return msalInstance;
  } catch (error) {
    console.error("Failed to initialize MSAL:", error);
    throw error;
  }
};

/**
 * Get the current MSAL instance (synchronous)
 * Returns null if not yet initialized
 * @returns {PublicClientApplication|null}
 */
export const getMsalInstance = () => msalInstance;

/**
 * Get MSAL instance, initializing if needed from cached config
 * @returns {PublicClientApplication|null}
 */
export const getOrCreateMsalInstance = () => {
  if (msalInstance) {
    return msalInstance;
  }

  // Try to create from cached config
  const cachedConfig = getCachedConfig();
  if (cachedConfig) {
    const msalConfig = createMsalConfig(cachedConfig);
    msalInstance = new PublicClientApplication(msalConfig);
    return msalInstance;
  }

  return null;
};

// Add scopes for ID token to be used at Microsoft identity platform endpoints
export const loginRequest = {
  scopes: ["https://analysis.windows.net/powerbi/api/.default"],
};

// Microsoft Graph API endpoint
export const graphConfig = {
  graphMeEndpoint: "https://graph.microsoft.com/v1.0/me",
};

// Export for backward compatibility - this will be null until initialized
export default msalInstance;
